import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import type { OrganizationOverviewDTO } from "@/orval/model";
import {
  getOrganizationManagementGetAllOrganizationsQueryKey,
  organizationManagementGetAllOrganizations,
} from "@/orval/organization-management";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { OrganizationDetailDialog } from "./-components/organization-detail-dialog";
import { format, parseISO } from "date-fns";
import { PortalPageHeader } from "@/components/portal-page-header";

const columns: ColumnDef<OrganizationOverviewDTO>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Organization Name" />,
    enableSorting: true,
    size: 200,
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return <span className="font-medium">{name}</span>;
    },
  },
  {
    accessorKey: "userCount",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Users" />,
    enableSorting: true,
    size: 100,
    cell: ({ getValue }) => {
      const count = getValue() as number;
      return <span>{count.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: "classCount",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Classes" />,
    enableSorting: true,
    size: 100,
    cell: ({ getValue }) => {
      const count = getValue() as number;
      return <span>{count.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created Date" />,
    enableSorting: true,
    size: 120,
    cell: ({ getValue }) => {
      const date = getValue() as string;
      return <span>{format(parseISO(date), "MMM dd, yyyy")}</span>;
    },
  },
];

const SiteAdminOrganizationsPage = () => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleRowClick = (organization: OrganizationOverviewDTO) => {
    setSelectedOrganizationId(organization.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrganizationId(null);
  };

  const getOrganizations = organizationManagementGetAllOrganizations;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Organization Management"
        subtitle="Manage all organizations across the platform"
      />

      <DataTable
        columns={columns}
        queryFn={(params, abortSignal) => getOrganizations(params, abortSignal)}
        queryKeyFn={(params) => getOrganizationManagementGetAllOrganizationsQueryKey(params)}
        onRowClick={handleRowClick}
        defaultSorting={[{ id: "createdAt", desc: true }]}
        getRowIdFn={(row) => row.id.toString()}
      />

      <OrganizationDetailDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        organizationId={selectedOrganizationId}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/site-admin/organizations")({
  component: SiteAdminOrganizationsPage,
});
